import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Building2, Users, ClipboardCheck, Radio, Map as MapIcon, Receipt, CheckCircle2, XCircle } from "lucide-react";

const ROLES = [
  {
    id: "executives",
    Icon: Building2,
    title: "Executives",
    desc: "Revenue visibility, production trends, SLA performance, damage risk, and market comparisons — without waiting for a weekly report.",
    bullets: ["Live revenue & production KPIs", "Market-by-market SLA performance", "Damage risk exposure tracking", "Quarter-over-quarter comparisons"],
    panel: "kpi",
  },
  {
    id: "market",
    Icon: Users,
    title: "Market Managers",
    desc: "Staffing vs. workload balance, ticket aging, past-due trends, and productivity per locator per day.",
    bullets: ["Workload balance per locator", "Aging-ticket heatmap", "Past-due trend analysis", "Daily production per locator"],
    panel: "bars",
  },
  {
    id: "supervisors",
    Icon: ClipboardCheck,
    title: "Supervisors",
    desc: "Crew assignment, QA review queue, closeout approval, and field status in one screen.",
    bullets: ["QA review queue", "Approve / reject closeouts", "Crew status board", "Required-evidence checklist"],
    panel: "qa",
  },
  {
    id: "dispatchers",
    Icon: Radio,
    title: "Dispatchers",
    desc: "Live ticket queue, priority sorting, route planning, and urgent ticket escalation.",
    bullets: ["Priority-sorted queue", "One-click assignment", "Route optimization", "Urgent escalation"],
    panel: "dispatch",
  },
  {
    id: "locators",
    Icon: MapIcon,
    title: "Locators",
    desc: "Clean mobile work list, turn-by-turn map, ticket details, photo upload, notes, and one-tap completion.",
    bullets: ["Today's prioritized list", "Turn-by-turn navigation", "GPS-tagged photo upload", "One-tap closeout"],
    panel: "mobile",
  },
  {
    id: "billing",
    Icon: Receipt,
    title: "Billing & Admin",
    desc: "Proof packages, ticket history, invoice-ready closeout data, and customer-specific reporting.",
    bullets: ["Auto-formatted invoices", "Full ticket history", "Customer-specific rules", "Audit-grade evidence"],
    panel: "billing",
  },
];

function RolePanel({ kind }) {
  if (kind === "kpi") {
    const KPIS = [
      { l: "Total Tickets", v: "12,481", t: "+8.2%" },
      { l: "Completion Rate", v: "94.2%", t: "+1.4%" },
      { l: "SLA % Met", v: "91.8%", t: "-0.3%" },
      { l: "At-Fault Risk", v: "0.42%", t: "-12%" },
    ];
    return (
      <div className="grid grid-cols-2 gap-3">
        {KPIS.map((k) => (
          <div key={k.l} className="rounded-xl bg-[#0a0a0a] border border-white/5 p-4">
            <div className="text-xs text-[#94A3B8]">{k.l}</div>
            <div className="font-mono font-bold text-2xl text-white mt-1">{k.v}</div>
            <div className={`text-[11px] mt-1 ${k.t.startsWith("-") && !k.l.includes("Risk") ? "text-[#EF4444]" : "text-[#22C55E]"}`}>{k.t}</div>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "bars") {
    const DATA = [
      { n: "Marcus T", v: 42 },
      { n: "Jordan R", v: 38 },
      { n: "Sarah K", v: 51 },
      { n: "Devon M", v: 29 },
      { n: "Priya L", v: 44 },
    ];
    const max = 60;
    return (
      <div className="space-y-2.5">
        {DATA.map((d) => (
          <div key={d.n} className="flex items-center gap-3 text-xs">
            <span className="w-20 text-white">{d.n}</span>
            <div className="flex-1 h-3 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#FB923C] to-[#F97316]"
                style={{ width: `${(d.v / max) * 100}%` }}
              />
            </div>
            <span className="font-mono w-8 text-right text-white">{d.v}</span>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "qa") {
    return (
      <div className="space-y-2">
        {[
          { id: "LOC-23905", ok: true },
          { id: "LOC-23901", ok: false },
          { id: "LOC-23877", ok: true },
          { id: "LOC-23899", ok: true },
        ].map((q) => (
          <div key={q.id} className="flex items-center justify-between rounded-md bg-[#0a0a0a] border border-white/5 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-white">{q.id}</span>
              <span className={`status-pill ${q.ok ? "bg-[#22C55E]/15 text-[#22C55E]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>
                {q.ok ? "READY" : "MISSING"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button className="h-7 w-7 grid place-items-center rounded-md bg-[#22C55E]/15 text-[#22C55E]"><CheckCircle2 size={14} /></button>
              <button className="h-7 w-7 grid place-items-center rounded-md bg-[#EF4444]/15 text-[#EF4444]"><XCircle size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "dispatch") {
    return (
      <div className="space-y-2">
        {[
          { id: "LOC-23877", a: "Chicago, IL", p: "High" },
          { id: "LOC-23914", a: "Atlanta, GA", p: "High" },
          { id: "LOC-23921", a: "Nashville, TN", p: "Med" },
        ].map((t) => (
          <div key={t.id} className="flex items-center justify-between rounded-md bg-[#0a0a0a] border border-white/5 px-3 py-2.5">
            <div>
              <div className="font-mono text-sm text-white">{t.id}</div>
              <div className="text-[10px] text-[#94A3B8]">{t.a}</div>
            </div>
            <button className="text-xs font-bold bg-[#F97316] text-white px-3 py-1.5 rounded-md">ASSIGN</button>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "mobile") {
    return <MobileFrameMini />;
  }
  // billing
  return (
    <div className="rounded-md bg-[#0a0a0a] border border-white/5 overflow-hidden">
      <div className="px-3 py-2 border-b border-white/5 text-xs font-semibold text-white">Closeout Summary · Oct 2025</div>
      <div className="divide-y divide-white/5 text-xs">
        {[
          ["Georgia Power", "$48,210"],
          ["Piedmont Gas", "$32,580"],
          ["ComEd", "$61,940"],
          ["MLGW", "$24,310"],
        ].map(([k, v]) => (
          <div key={k} className="flex items-center justify-between px-3 py-2">
            <span className="text-[#94A3B8]">{k}</span>
            <span className="font-mono text-[#22C55E]">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MobileFrameMini() {
  return (
    <div className="mx-auto w-48 h-[320px] rounded-[28px] bg-[#000000] border-2 border-white/10 p-1.5 shadow-[0_20px_60px_rgba(249,115,22,0.4)]">
      <div className="absolute"></div>
      <div className="h-full rounded-[20px] bg-[#0a0a0a] p-3 overflow-hidden">
        <div className="text-[10px] text-[#94A3B8] mb-1">TODAY · 6 TICKETS</div>
        {[
          { id: "LOC-2391", a: "412 Peachtree St", st: "IN PROGRESS", c: "bg-[#FB923C]/20 text-[#FDBA74]" },
          { id: "LOC-2390", a: "88 N Lawn Ave", st: "PENDING", c: "bg-[#F97316]/20 text-[#F97316]" },
          { id: "LOC-2389", a: "204 Elm Rd", st: "COMPLETE", c: "bg-[#22C55E]/20 text-[#22C55E]" },
        ].map((t) => (
          <div key={t.id} className="mt-1.5 rounded-md bg-[#000000] border border-white/5 p-2">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] text-white">{t.id}</span>
              <span className={`status-pill text-[9px] ${t.c}`}>{t.st}</span>
            </div>
            <div className="text-[10px] text-[#94A3B8] mt-1">{t.a}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RoleTabs() {
  const [tab, setTab] = useState("executives");

  return (
    <section data-testid="roles-section" className="section-y relative">
      <div className="container-x">
        <div className="max-w-3xl reveal mb-12">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#F97316] mb-3">Roles</div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
            Built for every role in your operation.
          </h2>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="reveal">
          <TabsList className="flex flex-wrap h-auto bg-[#171717]/60 border border-white/10 rounded-2xl p-1.5 gap-1">
            {ROLES.map((r) => {
              const Icon = r.Icon;
              return (
                <TabsTrigger
                  key={r.id}
                  value={r.id}
                  data-testid={`role-tab-${r.id}`}
                  className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm text-[#94A3B8] data-[state=active]:bg-[#F97316] data-[state=active]:text-white data-[state=active]:shadow-[0_4px_20px_rgba(249,115,22,0.4)]"
                >
                  <Icon size={14} />
                  {r.title}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {ROLES.map((r) => (
            <TabsContent key={r.id} value={r.id} className="mt-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-2xl glass p-7">
                  <h3 className="font-display font-bold text-2xl text-white">{r.title}</h3>
                  <p className="mt-3 text-[#94A3B8] leading-relaxed">{r.desc}</p>
                  <ul className="mt-5 space-y-2.5">
                    {r.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm text-white/90">
                        <CheckCircle2 size={16} className="text-[#22C55E] mt-0.5 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl glass p-6 relative">
                  <div className="text-[10px] font-mono text-[#94A3B8] mb-4 uppercase tracking-widest">Live preview</div>
                  <RolePanel kind={r.panel} />
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}
